import{useState,useEffect}from"react";
import type{ReactNode}from"react";
import{AppShell}from"./components/AppShell";
import{OperationsShell}from"./components/OperationsShell";
import{PersonalSecurity}from"./features/security/PersonalSecurity";
import{VaultWorkspace}from"./features/vault/VaultWorkspace";
import{SecurityOverview}from"./features/security/SecurityOverview";
import{IdentityAccessWorkspace}from"./features/security/IdentityAccessWorkspace";
import{ProviderMarketWorkspace}from"./features/provider-market/ProviderMarketWorkspace";
import HarnessProviderSettings from"./features/harness/HarnessProviderSettings";
import{RegistrationsView}from"./features/security/RegistrationsView";
import{PersonDetail}from"./features/security/PersonDetail";
import{PasskeyDetail}from"./features/security/PasskeyDetail";
import{AgentWorkspace}from"./features/agent/AgentWorkspace";
import{AuthGate}from"./features/auth/AuthGate";
import{LiDPage}from"./features/learning-real/LiDPage";

import{
  AuditView,DevicesView,PoliciesView,RecoveryView,RequestsView,
  SessionsView,SettingsView,UsersView,VaultsView
}from"./features/security/ResourceViews";
import{authApi}from"./lib/authApi";
import type{Me}from"./lib/authApi";
import type{SecuritySection,WorkspaceMode}from"./lib/types";
import{useAppI18n}from"./lib/appI18n";
import BirlesikPanel from "./features/repository-center/BirlesikPanel";

type PersonalSurface="security"|"vault";

function LoadingLanding(){
 return(
  <div style={{
   minHeight:"100vh",
   background:"#0b0d10",
   color:"#f5f7fa",
   display:"flex",
   alignItems:"center",
   justifyContent:"center",
   fontFamily:"Inter, system-ui, sans-serif"
  }}>
   <div style={{
    fontSize:"22px",
    fontWeight:600,
    letterSpacing:"0.08em"
   }}>
    KNSAIX
   </div>
  </div>
 );
}

function workspaceAllowed(
 identity:Me,
 key:string,
){
 return Boolean(
  identity.authorization?.workspaces?.some(
   workspace=>workspace.key===key
  )
 );
}

function AccessPending({identity}:{identity:Me}){
 return(
  <div style={{
   minHeight:"100vh",
   background:"#0b0d10",
   color:"#f5f7fa",
   display:"flex",
   alignItems:"center",
   justifyContent:"center",
   fontFamily:"Inter, system-ui, sans-serif",
   padding:"24px"
  }}>
   <div style={{maxWidth:"560px"}}>
    <div style={{
     fontSize:"14px",
     opacity:.65,
     letterSpacing:".12em",
     marginBottom:"14px"
    }}>
     KNSAIX
    </div>
    <h1>Access pending</h1>
    <p style={{lineHeight:1.6,opacity:.8}}>
     Your account is active, but no workspace has been assigned yet.
     A KNSAIX administrator can assign the appropriate role,
     access package or capability from Security Center.
    </p>
    <p style={{opacity:.6,fontSize:"14px"}}>
     Signed in as {identity.email}
    </p>
    <button
     type="button"
     onClick={()=>void authApi.logout().then(()=>window.location.replace("/"))}
     style={{
      marginTop:"18px",
      padding:"10px 16px",
      borderRadius:"8px",
      border:"1px solid #39414d",
      background:"#171b21",
      color:"#f5f7fa",
      cursor:"pointer",
      fontWeight:600
     }}
    >
     Sign out
    </button>
   </div>
  </div>
 );
}


function HarnessEntry(){
 const[error,setError]=useState<string|null>(null);
 useEffect(()=>{
  let cancelled=false;
  void fetch("/api/v1/harness/entry",{method:"POST",credentials:"include",headers:{Accept:"application/json"}})
   .then(async r=>{if(!r.ok){let d=`request_failed_${r.status}`;try{const b=await r.json();if(typeof b?.detail==="string")d=b.detail}catch{}throw new Error(d)}return await r.json() as {location:string}})
   .then(x=>{if(!cancelled)window.location.replace(x.location)})
   .catch(e=>{if(!cancelled)setError(e instanceof Error?e.message:"harness_entry_failed")});
  return()=>{cancelled=true};
 },[]);
 if(error)return <div className="auth-screen"><main className="auth-card"><div className="auth-brand">KNSAIX</div><h1>Harness unavailable</h1><p className="auth-error">{error}</p><button className="auth-secondary" type="button" onClick={()=>window.location.assign("/")}>Back</button></main></div>;
 return <LoadingLanding/>;
}



function OpenCodeEntry(){
 const{t}=useAppI18n();
 const[error,setError]=useState<string|null>(null);

 useEffect(()=>{
  let cancelled=false;

  void fetch(
   "/api/v1/auth/opencode/entry",
   {
    method:"POST",
    credentials:"include",
    headers:{
     Accept:"application/json"
    }
   }
  )
   .then(async r=>{
    if(!r.ok){
     let detail=`request_failed_${r.status}`;

     try{
      const body=await r.json();

      if(typeof body?.detail==="string"){
       detail=body.detail;
      }
     }catch{}

     throw new Error(detail);
    }

    return await r.json() as {
     location:string
    };
   })
   .then(result=>{
    if(!cancelled){
     window.location.replace(
      result.location
     );
    }
   })
   .catch(error=>{
    if(!cancelled){
     setError(
      error instanceof Error
       ?error.message
       :"opencode_entry_failed"
     );
    }
   });

  return()=>{
   cancelled=true;
  };
 },[]);

 if(error){
  return(
   <div className="auth-screen">
    <main className="auth-card">
     <div className="auth-brand">
      KNSAIX
     </div>

     <h1>{t("entry.opencodeUnavailable")}</h1>

     <p className="auth-error">
      {error}
     </p>

     <button
      className="auth-secondary"
      type="button"
      onClick={()=>
       window.location.assign("/")
      }
     >
      Back
     </button>
    </main>
   </div>
  );
 }

 return <LoadingLanding/>;
}


function OpenDesignEntry(){
 const{t}=useAppI18n();
 const[error,setError]=useState<string|null>(null);

 useEffect(()=>{
  let cancelled=false;

  void fetch(
   "/api/v1/auth/opendesign/entry",
   {
    method:"POST",
    credentials:"include",
    headers:{
     Accept:"application/json"
    }
   }
  )
   .then(async r=>{
    if(!r.ok){
     let detail=`request_failed_${r.status}`;

     try{
      const body=await r.json();

      if(typeof body?.detail==="string"){
       detail=body.detail;
      }
     }catch{}

     throw new Error(detail);
    }

    return await r.json() as {location:string};
   })
   .then(x=>{
    if(!cancelled){
     window.location.replace(x.location);
    }
   })
   .catch(e=>{
    if(!cancelled){
     setError(
      e instanceof Error
       ?e.message
       :"opendesign_entry_failed"
     );
    }
   });

  return()=>{cancelled=true};
 },[]);

 if(error){
  return(
   <div className="auth-screen">
    <main className="auth-card">
     <div className="auth-brand">KNSAIX</div>
     <h1>{t("entry.opendesignUnavailable")}</h1>
     <p className="auth-error">{error}</p>
     <button
      className="auth-secondary"
      type="button"
      onClick={()=>window.location.assign("/")}
     >
      Back
     </button>
    </main>
   </div>
  );
 }

 return <LoadingLanding/>;
}


function KestraEntry(){
 const{t}=useAppI18n();
 const[error,setError]=useState<string|null>(null);
 useEffect(()=>{
  let cancelled=false;
  void fetch("/api/v1/auth/kestra/entry",{method:"POST",credentials:"include",headers:{Accept:"application/json"}})
   .then(async r=>{if(!r.ok){let d=`request_failed_${r.status}`;try{const b=await r.json();if(typeof b?.detail==="string")d=b.detail}catch{}throw new Error(d)}return await r.json() as {location:string}})
   .then(x=>{if(!cancelled)window.location.replace(x.location)})
   .catch(e=>{if(!cancelled)setError(e instanceof Error?e.message:"kestra_entry_failed")});
  return()=>{cancelled=true};
 },[]);
 if(error)return <div className="auth-screen"><main className="auth-card"><div className="auth-brand">KNSAIX</div><h1>{t("entry.kestraUnavailable")}</h1><p className="auth-error">{error}</p><button className="auth-secondary" type="button" onClick={()=>window.location.assign("/")}>Back</button></main></div>;
 return <LoadingLanding/>;
}

function RootLanding({identity}:{identity:Me}){
 const workspaces=identity.authorization?.workspaces||[];

 if(workspaces.length===0){
  return <AccessPending identity={identity}/>;
 }

 if(workspaces.length===1){
  window.location.replace(workspaces[0].route);
  return <LoadingLanding/>;
 }

 return(
  <div className="auth-screen">
   <main className="auth-card auth-card-wide" aria-live="polite">
    <div className="auth-brand">KNSAIX</div>
    <h1>Choose a workspace</h1>
    <p className="auth-copy">
     Select where you want to continue. Your available workspaces are determined by your current authorization.
    </p>

    <div style={{display:"grid",gap:"12px",marginTop:"20px"}}>
     {workspaces.map(workspace=>(
      <button
       key={workspace.key}
       type="button"
       className="auth-secondary"
       style={{textAlign:"left",padding:"16px"}}
       onClick={()=>window.location.assign(workspace.route)}
      >
       <strong>{workspace.display_name}</strong>
      </button>
     ))}
    </div>

    <button
     type="button"
     className="auth-link"
     onClick={()=>void authApi.logout().then(()=>window.location.replace("/"))}
    >
     Sign out
    </button>
   </main>
  </div>
 );
}

function WorkspaceGuard({
 identity,
 workspaceKey,
 children,
}:{
 identity:Me;
 workspaceKey:string;
 children:()=>ReactNode;
}){
 if(!workspaceAllowed(identity,workspaceKey)){
  window.location.replace("/");
  return <LoadingLanding/>;
 }

 return <>{children()}</>;
}

function SecurityWorkspace(){
 const[mode,setMode]=useState<WorkspaceMode>("admin");
/* KNSAIX_SECURITY_URL_STATE_V2 */
 /* KNSAIX_PERSON_DETAIL_ROUTE_V1 */
 const securitySections:SecuritySection[]=[
   "overview","users","registrations","devices","vaults","access",
   "requests","sessions","recovery","audit","policies","settings",
  ];
  const searchParams=
   typeof window!=="undefined"
    ?new URLSearchParams(window.location.search)
    :new URLSearchParams();
  const requestedSecuritySection=searchParams.get("section");
  const initialSecuritySection:SecuritySection=
   requestedSecuritySection
   &&securitySections.includes(requestedSecuritySection as SecuritySection)
    ?requestedSecuritySection as SecuritySection
    :"overview";
  // Person Detail and Passkey Detail are each addressable on their own via
  // the security section plus a subject or credential search parameter.
  // Both survive a full page load because they are restored from location.search.
  const[personSubjectId,setPersonSubjectId]=
   useState<string|null>(initialSecuritySection==="users"?searchParams.get("subject"):null);
  const[passkeyCredentialId,setPasskeyCredentialId]=
   useState<string|null>(initialSecuritySection==="devices"?searchParams.get("credential"):null);
  const[section,setSection]=
   useState<SecuritySection>(initialSecuritySection);

  const writeLocation=(
   nextSection:SecuritySection,
   nextSubject:string|null,
   nextCredential:string|null,
  )=>{
   if(typeof window==="undefined")return;
   const url=new URL(window.location.href);
   if(nextSection==="overview")url.searchParams.delete("section");
   else url.searchParams.set("section",nextSection);
   if(nextSubject)url.searchParams.set("subject",nextSubject);
   else url.searchParams.delete("subject");
   if(nextCredential)url.searchParams.set("credential",nextCredential);
   else url.searchParams.delete("credential");
   if(nextSection!=="provider-market")url.searchParams.delete("center");
   window.history.replaceState(null,"",url);
  };

  const openPerson=(subjectId:string)=>{
   setPersonSubjectId(subjectId);
   setPasskeyCredentialId(null);
   writeLocation("users",subjectId,null);
  };
  const closePerson=()=>{
   setPersonSubjectId(null);
   writeLocation("users",null,null);
  };
  const openPasskey=(credentialUuid:string)=>{
   setPasskeyCredentialId(credentialUuid);
   setPersonSubjectId(null);
   writeLocation("devices",null,credentialUuid);
  };
  const closePasskey=()=>{
   setPasskeyCredentialId(null);
   writeLocation("devices",null,null);
  };
  const goToSection=(next:SecuritySection)=>{
   setSection(next);
   setPersonSubjectId(null);
   setPasskeyCredentialId(null);
   writeLocation(next,null,null);
  };
  useEffect(()=>{
   if(typeof window==="undefined")return;
   const url=new URL(window.location.href);
   if(section==="overview")url.searchParams.delete("section");
   else url.searchParams.set("section",section);
  window.history.replaceState(
   null,
   "",
   `${url.pathname}${url.search}${url.hash}`
  );
 },[section]);
 const[personalSurface,setPersonalSurface]=
  useState<PersonalSurface>("security");

const changeMode=(next:WorkspaceMode)=>{
   setMode(next);
   setPersonalSurface("security");
   setPersonSubjectId(null);
   setPasskeyCredentialId(null);
   if(next==="admin")setSection("overview");
  };

  return(
   <AppShell
    mode={mode}
    onMode={changeMode}
    section={section}
    onSection={goToSection}
   >
    {mode==="personal"
     ?(
       personalSurface==="vault"
        ?(
          <VaultWorkspace
           onBack={()=>setPersonalSurface("security")}
          />
         )
        :(
          <PersonalSecurity
           onOpenVault={()=>setPersonalSurface("vault")}
          />
         )
      )
     :(
       <>
        {section==="overview"&&<SecurityOverview onNavigate={goToSection}/>}
        {section==="users"&&(
         personSubjectId
          ?(
           <PersonDetail
            subjectId={personSubjectId}
            onBack={closePerson}
            onOpenPasskey={openPasskey}
           />
          )
          :<UsersView onOpenPerson={openPerson}/>
        )}
       {section==="registrations"&&<RegistrationsView/>}
       {section==="devices"&&(
        passkeyCredentialId
         ?(
          <PasskeyDetail
           credentialUuid={passkeyCredentialId}
           onBack={closePasskey}
           onOpenPerson={openPerson}
          />
         )
         :<DevicesView onOpenPasskey={openPasskey}/>
       )}
       {section==="vaults"&&(
        <VaultsView
         onOpenVault={()=>{
          setMode("personal");
          setPersonalSurface("vault");
         }}
        />
       )}
       {section==="access"&&<IdentityAccessWorkspace/>}
       {section==="requests"&&<RequestsView/>}
       {section==="sessions"&&<SessionsView/>}
       {section==="recovery"&&<RecoveryView/>}
       {section==="audit"&&<AuditView/>}
       {section==="policies"&&<PoliciesView/>}
       {section==="settings"&&<SettingsView/>}
      </>
     )}
  </AppShell>
 );
}

function NotFound(){
 return(
  <div style={{
   minHeight:"100vh",
   background:"#0b0d10",
   color:"#f5f7fa",
   display:"flex",
   alignItems:"center",
   justifyContent:"center",
   fontFamily:"Inter, system-ui, sans-serif"
  }}>
   <div>
    <h1>Page not found</h1>
    <button
     type="button"
     onClick={()=>window.location.assign("/")}
    >
     Return to KNSAIX
    </button>
   </div>
  </div>
 );
}

export default function App(){
 const path=
  typeof window!=="undefined"
   ?window.location.pathname
   :"/";


 if(path==="/opencode"){
  return(
   <AuthGate>
    {identity=>(
     <WorkspaceGuard
      identity={identity}
      workspaceKey="OPENCODE"
     >
      {()=> <OpenCodeEntry/>}
     </WorkspaceGuard>
    )}
   </AuthGate>
  );
 }

 if(path==="/opendesign"){
  return(
   <AuthGate>
    {identity=>(
     <WorkspaceGuard
      identity={identity}
      workspaceKey="OPENDESIGN"
     >
      {()=> <OpenDesignEntry/>}
     </WorkspaceGuard>
    )}
   </AuthGate>
  );
 }

 if(path==="/kestra"){
  return(<AuthGate>{identity=>(<WorkspaceGuard identity={identity} workspaceKey="KESTRA">{()=> <KestraEntry/>}</WorkspaceGuard>)}</AuthGate>);
 }

 if(path==="/harness/settings"){
  return(<AuthGate>{identity=>(<WorkspaceGuard identity={identity} workspaceKey="HARNESS">{()=> <HarnessProviderSettings/>}</WorkspaceGuard>)}</AuthGate>);
 }

 if(path==="/harness"){
  return(<AuthGate>{identity=>(<WorkspaceGuard identity={identity} workspaceKey="HARNESS">{()=> <HarnessEntry/>}</WorkspaceGuard>)}</AuthGate>);
 }



 if(path==="/lid"){
  return(
   <AuthGate>
    {identity=>(
     <WorkspaceGuard
      identity={identity}
      workspaceKey="LID"
     >
      {()=> <LiDPage/>}
     </WorkspaceGuard>
    )}
   </AuthGate>
  );
 }

 if(path==="/"||path==="/login"){
  return(
   <AuthGate>
    {identity=><RootLanding identity={identity}/>}
   </AuthGate>
  );
 }

  /* KNSAIX_OPERATIONS_ROUTES_V2 */
 if(path==="/operations"){
  return(
   <AuthGate>
    {identity=>{
     const destination=
      workspaceAllowed(identity,"AGENT_CONTROL")
       ?"/operations/agent-control"
       :workspaceAllowed(identity,"SECURITY_CENTER")
        ?"/operations/provider-market"
        :"/";
     window.location.replace(destination);
     return <LoadingLanding/>;
    }}
   </AuthGate>
  );
 }


  if(path==="/operations/repository-center"){
    return (
      <AuthGate>
        {(me)=>(
          <WorkspaceGuard
            identity={me}
            workspaceKey="REPOSITORY_CENTER"
          >
            {()=>(
              <OperationsShell active="repository-center">
                <BirlesikPanel authenticatedUser={me}/>
              </OperationsShell>
            )}
          </WorkspaceGuard>
        )}
      </AuthGate>
    );
  }

 if(path==="/operations/provider-market"){
  return(
   <AuthGate>
    {identity=>(
     <WorkspaceGuard
      identity={identity}
      workspaceKey="SECURITY_CENTER"
     >
      {()=>(
       <OperationsShell active="provider-market">
        <ProviderMarketWorkspace/>
       </OperationsShell>
      )}
     </WorkspaceGuard>
    )}
   </AuthGate>
  );
 }

 if(path==="/operations/agent-control"){
  return(
   <AuthGate>
    {identity=>(
     <WorkspaceGuard
      identity={identity}
      workspaceKey="AGENT_CONTROL"
     >
      {()=>(
       <OperationsShell active="agent-control" immersive>
        <AgentWorkspace
         onSignOut={()=>window.location.assign("/")}
        />
       </OperationsShell>
      )}
     </WorkspaceGuard>
    )}
   </AuthGate>
  );
 }

if(path==="/agent-control"){
  return(
   <AuthGate>
    {identity=>(
     <WorkspaceGuard
      identity={identity}
      workspaceKey="AGENT_CONTROL"
     >
      {()=>(
       <AgentWorkspace
        onSignOut={()=>window.location.assign("/")}
       />
      )}
     </WorkspaceGuard>
    )}
   </AuthGate>
  );
 }

 if(path==="/agent-control-v2"){
  return(
   <AuthGate>
    {identity=>(
     <WorkspaceGuard
      identity={identity}
      workspaceKey="AGENT_CONTROL"
     >
      {()=>(
       <AgentWorkspace
        onSignOut={()=>window.location.assign("/")}
       />
      )}
     </WorkspaceGuard>
    )}
   </AuthGate>
  );
 }

 if(path==="/security"){
  return(
   <AuthGate>
    {identity=>(
     <WorkspaceGuard
      identity={identity}
      workspaceKey="SECURITY_CENTER"
     >
      {()=> <SecurityWorkspace/>}
     </WorkspaceGuard>
    )}
   </AuthGate>
  );
 }

 return <NotFound/>;
}
